// frontend/src/components/InternalProductList.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Drawer,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ProductFormDialog from "./ProductFormDialog";
import CustomizeColumnsDialog from "./CustomizeColumnsDialog";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  markOutOfStock,
  searchProducts,
  downloadProducts
} from "../api";
import { sendIndividualEmails, sendGroupEmail } from "../emailSender";
import * as XLSX from "xlsx";
import { jwtDecode } from "jwt-decode";

const InternalProductList = ({ onBack }) => {
  // state hooks
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("in");
  const [pageSize, setPageSize] = useState(50);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const drawerWidth = isMobile ? 150 : 200;
  const timezone = "America/New_York";

  // Force visibility of ROI and Cust Cost MOQ on mount
  useEffect(() => {
    setColumnVisibilityModel(model => ({
      ...model,
      roi: true,
      cust_cost_moq: true
    }));
  }, []);

  // Initialize columnVisibilityModel (from localStorage or default all true)
  useEffect(() => {
    const saved = localStorage.getItem("columnVisibilityModel");
    const defaultVis = baseColumns.reduce((acc, col) => {
      acc[col.field] = true;
      return acc;
    }, {});
    if (saved) {
      const parsed = JSON.parse(saved);
      setColumnVisibilityModel({ ...defaultVis, ...parsed });
    } else {
      setColumnVisibilityModel(defaultVis);
      localStorage.setItem("columnVisibilityModel", JSON.stringify(defaultVis));
    }
  }, []);

  // Persist columnVisibilityModel on changes
  useEffect(() => {
    if (Object.keys(columnVisibilityModel).length) {
      localStorage.setItem("columnVisibilityModel", JSON.stringify(columnVisibilityModel));
    }
  }, [columnVisibilityModel]);

  // Auth & initial load
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) throw new Error();
      loadProducts();
    } catch {
      localStorage.clear();
      navigate("/login");
    }
  }, [navigate]);

  // Sort model persistence
  useEffect(() => {
    const saved = localStorage.getItem("sortModel");
    if (saved) setSortModel(JSON.parse(saved));
    else {
      const _def = [{ field: "offer_date", sort: "desc" }];
      setSortModel(_def);
      localStorage.setItem("sortModel", JSON.stringify(_def));
    }
  }, []);

  // Fetch products
  async function loadProducts() {
    setLoading(true);
    try {
      const resp = await fetchProducts();
      const list = Array.isArray(resp.products) ? resp.products : [];
      setProducts(list);
      setFilteredProducts(list.filter(p => !p.out_of_stock));
    } catch (err) {
      console.error(err);
      localStorage.clear();
      navigate("/login");
    } finally {
      setLoading(false);
    }
  }

  // Search & filter
  useEffect(() => {
    if (!searchQuery.trim()) return applyFilters();
    const q = searchQuery.toLowerCase();
    setFilteredProducts(
      products.filter(p =>
        Object.values(p).some(v => v != null && v.toString().toLowerCase().includes(q))
      )
    );
  }, [searchQuery, products]);

  function applyFilters() {
    let tmp = [...products];
    if (categoryFilter) tmp = tmp.filter(p => (p.category || "").toLowerCase() === categoryFilter.toLowerCase());
    if (vendorFilter)   tmp = tmp.filter(p => (p.vendor   || "").toLowerCase() === vendorFilter.toLowerCase());
    if (stockFilter === "in")  tmp = tmp.filter(p => !p.out_of_stock);
    if (stockFilter === "out") tmp = tmp.filter(p => p.out_of_stock);
    setFilteredProducts(tmp);
  }

  function resetFilters() {
    setCategoryFilter("");
    setVendorFilter("");
    setStockFilter("in");
    setFilteredProducts(products.filter(p => !p.out_of_stock));
  }

  // Selection & emails
  const handleSelectionModelChange = ids => setSelectedIds(ids);
  async function handleSendIndividualEmails() {
    if (!selectedIds.length) return alert("Select at least one.");
    await sendIndividualEmails(filteredProducts.filter(p => selectedIds.includes(String(p.id))));
    loadProducts();
  }
  async function handleSendGroupEmail() {
    if (!selectedIds.length) return alert("Select at least one.");
    await sendGroupEmail(filteredProducts.filter(p => selectedIds.includes(String(p.id))));
    loadProducts();
  }

  // CRUD dialogs
  const handleAddClick  = () => { setDialogMode("add");  setSelectedProduct(null); setDialogOpen(true); };
  const handleEditClick = p => { setDialogMode("edit"); setSelectedProduct(p);    setDialogOpen(true); };
  async function handleFormSubmit(data) {
    try {
      if (dialogMode === "add") {
        await createProduct(data);
      } else {
        await updateProduct(selectedProduct.id, data);
      }
    } catch (err) {
      console.error("Failed to save product:", err);
    } finally {
      setDialogOpen(false);
      loadProducts();
    }
  }
  const handleDialogClose = () => setDialogOpen(false);

  async function handleDelete(id) {
    if (!window.confirm("Delete?")) return;
    await deleteProduct(id);
    loadProducts();
  }
  async function handleMarkOutOfStock(id) {
    if (!window.confirm("Out-of-stock?")) return;
    await markOutOfStock(id);
    loadProducts();
  }

  // Download helper
  const handleDownloadProducts = async () => {
    try {
      await downloadProducts();
      alert("Products downloaded successfully");
    } catch (error) {
      alert("Failed to download products: " + error.message);
    }
  };

  // Inventory download (unchanged)
  const handleDownloadInventory = () => {
    // ... your existing logic
  };

  // Columns definition, with ROI and Cust Cost MOQ
  const baseColumns = [
    { field: "title", headerName: "Title", width: 200, editable: false },
    { field: "category", headerName: "Category", width: 200, editable: false },
    { field: "vendor_id", headerName: "Vendor ID", width: 120, editable: false },
    { field: "vendor", headerName: "Vendor", width: 150, editable: false },
    { field: "price", headerName: "Price", width: 100, type: "number", editable: false },
    { field: "cost",  headerName: "Cost",  width: 100, type: "number", editable: false },
    { field: "moq",   headerName: "MOQ",   width: 100, type: "number", editable: false },
    { field: "qty",   headerName: "Qty",   width: 100, type: "number", editable: false },
    { field: "upc",   headerName: "UPC",   width: 120, editable: false },
    { field: "asin",  headerName: "ASIN",  width: 120, editable: false },
    { field: "lead_time", headerName: "Lead Time", width: 120, editable: false },
    { field: "exp_date",  headerName: "Exp Date",  width: 120, editable: false },
    { field: "fob",       headerName: "FOB",       width: 100, editable: false },
    { field: "image_url", headerName: "Image URL", width: 200, editable: false },
    { field: "out_of_stock", headerName: "Out of Stock", width: 110, type: "boolean", editable: false },
    { field: "amazon_url",  headerName: "Amazon URL", width: 200, editable: false },
    { field: "walmart_url", headerName: "Walmart URL", width: 200, editable: false },
    { field: "ebay_url",    headerName: "eBay URL",    width: 200, editable: false },
    {
      field: "offer_date",
      headerName: "Offer Date",
      width: 180,
      editable: false,
      valueFormatter: params =>
        params.value
          ? new Date(params.value).toLocaleString("en-US", { timeZone: timezone })
          : ""
    },
    {
      field: "last_sent",
      headerName: "Last Sent",
      width: 180,
      editable: false,
      valueFormatter: params =>
        params.value
          ? new Date(params.value).toLocaleString("en-US", { timeZone: timezone })
          : ""
    },
    { field: "sales_per_month", headerName: "Sales Per Month", width: 150, type: "number", editable: false },
    { field: "net", headerName: "Net", width: 150, type: "number", editable: false },

    // ---- new computed fields ----
    {
      field: "roi",
      headerName: "ROI (%)",
      width: 150,
      type: "number",
      editable: false,
      valueGetter: params => {
        const net   = Number(params.row.net);
        const price = Number(params.row.price);
        return (!isNaN(net) && !isNaN(price) && price !== 0)
          ? (net / price) * 100
          : 0;
      },
      valueFormatter: params =>
        params.value != null ? `${params.value.toFixed(2)}%` : ""
    },
    {
      field: "cust_cost_moq",
      headerName: "Cust Cost per MOQ",
      width: 180,
      type: "number",
      editable: false,
      valueGetter: params => {
        const price = Number(params.row.price);
        const moq   = Number(params.row.moq);
        return (!isNaN(price) && !isNaN(moq)) ? price * moq : 0;
      }
    },

    // ---- actions column ----
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      renderCell: params => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => handleEditClick(params.row)}>Edit</Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>Delete</Button>
          {!params.row.out_of_stock && (
            <Button size="small" color="warning" onClick={() => handleMarkOutOfStock(params.row.id)}>
              Out of Stock
            </Button>
          )}
        </Stack>
      )
    }
  ];

  const columns = baseColumns.map(c => c);

  const allCategories = [
    "Appliances","Automotive","Baby","Beauty & Personal Care","Cell Phones & Accessories",
    "Clothing, Shoes & Jewelry","Electronics","Health & Household","Home & Kitchen",
    "Industrial & Scientific","Kitchen & Dining","Musical Instruments","Patio, Lawn & Garden",
    "Pet Supplies","Sports & Outdoors","Tools & Home Improvement","Toys & Games",
    "Office Products","Grocery & Gourmet Food","Video Games","Arts, Crafts & Sewing","Camera & Photo"
  ];

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box"
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {onBack && (
            <Button variant="contained" onClick={onBack} sx={{ mb: 2 }}>
              Back to Public View
            </Button>
          )}
          <Button variant="contained" color="secondary" onClick={handleLogout} sx={{ mb: 2 }}>
            Logout
          </Button>
          <Typography variant="h6" gutterBottom>
            Filters & Actions
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={e => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">(All)</MenuItem>
                {allCategories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="dense"
              label="Vendor"
              value={vendorFilter}
              onChange={e => setVendorFilter(e.target.value)}
              placeholder="Enter Vendor Name"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Stock Status</InputLabel>
              <Select
                value={stockFilter}
                label="Stock Status"
                onChange={e => setStockFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="in">In Stock</MenuItem>
                <MenuItem value="out">Out of Stock</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outlined" onClick={resetFilters}>Reset Filters</Button>
          </Stack>
          <Stack spacing={1}>
            <Button variant="contained" onClick={handleAddClick}>Add Product</Button>
            <Button variant="contained" color="success" onClick={handleSendIndividualEmails}>
              Send Individual Emails
            </Button>
            <Button variant="contained" color="success" onClick={handleSendGroupEmail}>
              Send Group Email
            </Button>
            <Button variant="contained" color="primary" onClick={handleDownloadInventory}>
              Download Inventory
            </Button>
            <Button variant="contained" color="primary" onClick={handleDownloadProducts}>
              Download Products
            </Button>
            <Button variant="contained" color="secondary" onClick={() => setCustomizeDialogOpen(true)}>
              Customize Columns
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {isMobile && (
                <IconButton onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Internal Product List
              </Typography>
            </Box>
            <TextField
              label="Search All Fields"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div style={{ height: isMobile ? "calc(100vh - 150px)" : 600, width: "100%" }}>
              <DataGrid
                rows={filteredProducts}
                columns={columns}
                getRowId={row => String(row.id)}
                checkboxSelection
                selectionModel={selectedIds}
                onSelectionModelChange={handleSelectionModelChange}
                pagination
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[5,10,25,50,100]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                sortModel={sortModel}
                onSortModelChange={model => {
                  setSortModel(model);
                  localStorage.setItem("sortModel", JSON.stringify(model));
                }}
              />
            </div>
          </>
        )}
      </Box>

      <ProductFormDialog
        open={dialogOpen}
        mode={dialogMode}
        initialData={selectedProduct}
        onClose={handleDialogClose}
        onSubmit={handleFormSubmit}
        timezone={timezone}
      />
      <CustomizeColumnsDialog
        open={customizeDialogOpen}
        onClose={() => setCustomizeDialogOpen(false)}
        columns={baseColumns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityChange={setColumnVisibilityModel}
      />
    </Box>
  );
};

export default InternalProductList;
